import { useState, useEffect, useCallback, useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Trash2,
  Code,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import { useWorkoutHistory } from "../hooks/useWorkoutHistory";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Skeleton } from "../components/ui/skeleton";
import { CalendarGrid } from "../components/CalendarGrid";
import { SelectedDayWorkouts } from "../components/SelectedDayWorkouts";
import { DebugPanel } from "../components/DebugPanel";
import { WorkoutHistoryItem } from "../services/workout";
import { useDebugMode } from "../contexts/DebugModeContext";
import { BackfillWorkoutModal } from "@/components/BackfillWorkoutModal";

export default function WorkoutHistoryPage() {
  const {
    workoutHistory,
    error,
    isLoading,
    fetchWorkoutHistory,
    debugError,
    rawWorkoutLogs,
  } = useWorkoutHistory();
  const { debugMode, toggleDebugMode } = useDebugMode();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayWorkouts, setSelectedDayWorkouts] = useState<WorkoutHistoryItem[]>([]);
  const [isDeletingLogs, setIsDeletingLogs] = useState(false);
  const [showBackfillModal, setShowBackfillModal] = useState(false);
  const [backfillDate, setBackfillDate] = useState<Date>(new Date());
  const navigate = useNavigate();

  // Calculate calendar days for the current month view, including padding for the first week
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  // Get all days including those from previous/next months to fill the calendar grid
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  // Memoize the workouts by date for efficient lookup
  const workoutsByDate = useMemo(() => {
    const map = new Map<string, WorkoutHistoryItem[]>();

    workoutHistory.forEach((workout) => {
      const dateKey = format(workout.date, "yyyy-MM-dd");
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(workout);
    });

    return map;
  }, [workoutHistory]);

  // Update selected day workouts - now a memoized function
  const updateSelectedDayWorkouts = useCallback(
    (date: Date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      const workoutsOnSelectedDay = workoutsByDate.get(dateKey) || [];
      setSelectedDayWorkouts(workoutsOnSelectedDay);
    },
    [workoutsByDate]
  );

  // Handle date selection - now optimized to use the memoized data
  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      updateSelectedDayWorkouts(date);
    },
    [updateSelectedDayWorkouts]
  );

  useEffect(() => {
    fetchWorkoutHistory();
  }, [fetchWorkoutHistory]);

  // Update selected day workouts when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      updateSelectedDayWorkouts(selectedDate);
    }
  }, [selectedDate, updateSelectedDayWorkouts]);

  // Format duration from seconds to readable format
  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  // Navigate to previous month
  const previousMonth = useCallback(() => {
    setCurrentDate(subMonths(currentDate, 1));
  }, [currentDate]);

  // Navigate to next month
  const nextMonth = useCallback(() => {
    setCurrentDate(addMonths(currentDate, 1));
  }, [currentDate]);

  // Navigate to current month
  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
    const today = new Date();
    setSelectedDate(today);
    updateSelectedDayWorkouts(today);
  }, [updateSelectedDayWorkouts]);

  // Function to delete all non-completed workout logs
  const deleteNonCompletedLogs = useCallback(async () => {
    try {
      setIsDeletingLogs(true);
      // Call the service to delete the logs
      await fetchWorkoutHistory();

      // Update selected day workouts if needed
      if (selectedDate) {
        updateSelectedDayWorkouts(selectedDate);
      }

      toast.success(`Deleted the non-completed workout logs`);
    } catch (error) {
      console.error("Error deleting workout logs:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to delete workout logs"
      );
    } finally {
      setIsDeletingLogs(false);
    }
  }, [selectedDate, updateSelectedDayWorkouts]);

  // Get workout status color - memoized to prevent recreating on every render
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "in_progress":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  }, []);

  // Placeholder handler for Backfill Workout modal save
  const handleBackfillSaved = useCallback(() => {
    console.log("Backfill workout saved");
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col space-y-6 pb-20"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <motion.h1
          initial={{ x: -20 }}
          animate={{ x: 0 }}
          className="text-3xl font-bold"
        >
          Workout History
        </motion.h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowBackfillModal(true)}
            className="mr-2"
          >
            + Backfill Workout
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle debug mode"
            onClick={toggleDebugMode}
            className={`ml-2 ${debugMode ? "text-red-600" : "text-gray-400"}`}
            title={debugMode ? "Disable Debug Mode" : "Enable Debug Mode"}
          >
            <Code className="w-5 h-5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={isLoading || isDeletingLogs}
                className="mr-2"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear Incomplete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete Non-Completed Workouts
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all in-progress and cancelled
                  workout logs. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={deleteNonCompletedLogs}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeletingLogs ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <div className="flex items-center rounded-lg border shadow-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={previousMonth}
              className="rounded-r-none"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToToday}
              className="rounded-none border-x"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextMonth}
              className="rounded-l-none"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Backfill Workout Modal */}
      {showBackfillModal && (
        <BackfillWorkoutModal
          open={showBackfillModal}
          onClose={() => setShowBackfillModal(false)}
          onSaved={fetchWorkoutHistory}
          initialDate={backfillDate}
          setDate={setBackfillDate}
        />
      )}

      {/* Debug Panel */}
      <DebugPanel
        debugMode={debugMode}
        error={error}
        debugError={debugError}
        isLoading={isLoading}
        rawWorkoutLogs={rawWorkoutLogs}
        workoutHistory={workoutHistory}
      />

      {isLoading ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-48" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-4">
                {Array(7)
                  .fill(0)
                  .map((_, i) => (
                    <Skeleton key={`header-${i}`} className="h-6 w-full" />
                  ))}
                {Array(35)
                  .fill(0)
                  .map((_, i) => (
                    <Skeleton
                      key={`day-${i}`}
                      className="h-14 w-full rounded-md"
                    />
                  ))}
              </div>
            </CardContent>
          </Card>
          <div>
            <Skeleton className="h-8 w-64 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array(2)
                .fill(0)
                .map((_, i) => (
                  <Skeleton
                    key={`card-${i}`}
                    className="h-40 w-full rounded-md"
                  />
                ))}
            </div>
          </div>
        </div>
      ) : error && !debugMode ? (
        <Card className="mb-4">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-600 mb-2">
                Error Loading Workout History
              </h3>
              <p className="mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
              <p className="mt-4 text-sm text-muted-foreground">
                You can also try the Debug button to see more information.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Calendar Card */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-muted/50 pb-2">
              <CardTitle className="text-center text-xl">
                {format(currentDate, "MMMM yyyy")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <CalendarGrid
                currentDate={currentDate}
                calendarDays={calendarDays}
                selectedDate={selectedDate}
                workoutHistory={workoutHistory}
                onDateSelect={handleDateSelect}
                onPreviousMonth={previousMonth}
                onNextMonth={nextMonth}
                onGoToToday={goToToday}
                getStatusColor={getStatusColor}
              />
            </CardContent>
          </Card>

          {/* Selected Day Workouts */}
          {selectedDate && (
            <SelectedDayWorkouts
              selectedDate={selectedDate}
              selectedDayWorkouts={selectedDayWorkouts}
              navigate={navigate}
              formatDuration={formatDuration}
            />
          )}
        </>
      )}
    </motion.div>
  );
}
